'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../api-client';
import { setCookie, getCookie, deleteCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import { User } from '@/types/server/user';
import { useQueryClient } from '@tanstack/react-query';

interface AuthResponse {
  access_token: string;
  user: User;
}

type AuthContextType = {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = getCookie('token') as string | undefined;
        const storedUser = getCookie('user') as string | undefined;
        if (storedToken) {
          setToken(storedToken);
        }
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
        if (!storedUser && storedToken) {
          const userData = await fetchUserData(storedToken);
          setUser(userData);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        deleteCookie('token');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const fetchUserData = async (authToken: string): Promise<User> => {
    try {
      const response = (await api
        .get('user', {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        })
        .json()) as { user: User };

      return response.user;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  };

  const login = async (
    email: string,
    password: string
  ): Promise<AuthResponse> => {
    try {
      setIsLoading(true);

      const response = (await api
        .post('auth/login', {
          json: { email, password, source: 'dashboard' },
          throwHttpErrors: true
        })
        .json()) as AuthResponse;

      if (!response.access_token || !response.user) {
        throw new Error('Invalid response from server');
      }

      setCookie('token', response.access_token, {
        path: '/',
        sameSite: 'lax',
        secure: process.env.NODE_ENV === 'production',
        maxAge: 30 * 24 * 60 * 60 // 30 days
      });

      setToken(response.access_token);
      setCookie('user', JSON.stringify(response.user), {
        path: '/',
        sameSite: 'lax',
        secure: process.env.NODE_ENV === 'production',
        maxAge: 30 * 24 * 60 * 60
      });
      setUser(response.user);

      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    deleteCookie('token');
    deleteCookie('user');
    setUser(null);
    setToken(null);
    queryClient.clear();
    router.push('/sign-in');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        logout,
        isAuthenticated: !!token
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
